/* eslint-disable react/jsx-props-no-spreading */
import { FormHelperText, Grid, InputLabel } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { addDays, addMinutes, format, subDays } from 'date-fns';
import { isEmpty } from 'lodash';
import { Field, Form, useFormState } from 'react-final-form';
import MultiDatePicker from 'react-multi-date-picker';

import { LoadingButton } from '@mui/lab';
import TextField from 'common/forms/TextField';
import { useFillHistorySearch } from '../hooks/useFillHistorySearch';

const StyledDatePicker = styled(MultiDatePicker)(({ theme }) => ({
  '& .rmdp-week-day': {
    color: theme.palette.primary.main,
    cursor: 'default',
    fontSize: '12px',
    fontWeight: 500,
  },
  '& .rmdp-input:focus': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '& .rmdp-range': {
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 0 3px #fff',
    color: '#fff',
  },
  '& .rmdp-range-hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '& .rmdp-day.rmdp-today span': {
    backgroundColor: theme.palette.primary.light,
    color: '#fff',
    borderColor: '#fff',
  },
  '& .rmdp-day:hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '& .rmdp-day:not(.rmdp-disabled,.rmdp-day-hidden) span:hover': {
    backgroundColor: theme.palette.primary.light,
    color: '#fff',
  },
  '& .rmdp-arrow-container:hover': {
    backgroundColor: theme.palette.primary.light,
    boxShadow: '0 0 3px #8798ad',
  },
  '& .rmdp-arrow': {
    border: `solid ${theme.palette.primary.light}`,
    borderWidth: '0 2px 2px 0',
    display: 'inline-block',
    marginTop: '5px',
    padding: '3px',
    width: '3px',
  },
  '&.rmdp-input': {
    padding: '50px',
  },
}));

const CustomDateRangeField = () => {
  const theme = useTheme();
  const { values: formValue, submitting } = useFormState();

  const shouldDisableDate = (date) => {
    if (isEmpty(formValue.dateRange)) return false;

    if (formValue.dateRange.length === 1) {
      const selectedDateObj = formValue.dateRange[0].toDate();
      const currentDate = date.toDate();

      // (13+1) 14 days, including the initial selected date.
      const minAllowedDate = subDays(selectedDateObj, 13);
      const maxAllowedDate = addDays(selectedDateObj, 13);

      return currentDate < minAllowedDate || currentDate > maxAllowedDate;
    }

    return false;
  };

  return (
    <Field
      name='dateRange'
      id='NdcFillHistory-dateRange-input'
      validate={(value) => {
        if (isEmpty(value) || value?.length < 2) {
          return 'Required';
        }

        return undefined;
      }}
      render={({ input, meta }) => {
        // showError needs to be a boolean, material type check this value
        const showError = !!meta.error && (!!meta.dirty || !!meta.touched);
        const displayedHelperText = showError && meta.error;

        return (
          <>
            <InputLabel
              htmlFor='NdcFillHistory-dateRange-input'
              sx={{ fontSize: '1em', mt: -0.5 }}
              error={showError}
              required
              disabled={submitting}
            >
              Fill Date Range
            </InputLabel>
            <StyledDatePicker
              id='NdcFillHistory-dateRange-input'
              name='dateRange'
              placeholder='MM/DD/YYYY ~ MM/DD/YYYY'
              format='MM/DD/YYYY'
              required
              maxDate={addMinutes(new Date(), 60)}
              highlightToday={false}
              editable={false}
              disabled={submitting}
              style={{
                width: '100%',
                minWidth: 210,
                height: '40px',
                padding: 10,
                margin: 0,
                marginBottom: 0,
                marginTop: 0,
                border: '1px solid',
                borderColor: showError ? theme.palette.error.main : theme.palette.grey[400],
                background: 'inherit',
                borderRadius: '3px',
              }}
              calendarPosition='bottom-center'
              range
              rangeHover
              mapDays={({ date }) => ({
                disabled: shouldDisableDate(date),
              })}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...input} // intentional spreading, injecting final-form
              onChange={(data) => {
                input.onChange(data);
              }}
            />
            <FormHelperText sx={{ color: theme.palette.error.main, pl: 1, mb: 0, mt: 0.5 }}>
              {displayedHelperText}
            </FormHelperText>
          </>
        );
      }}
    />
  );
};

const NdcSearch = () => {
  const { searchFillHistory } = useFillHistorySearch();

  const handleFillHistorySearch = ({ ndc, dateRange }) =>
    searchFillHistory({
      ndc,
      fillDateFrom: format(new Date(dateRange[0]), 'yyyy-MM-dd'),
      fillDateTo: format(new Date(dateRange[1]), 'yyyy-MM-dd'),
    });

  return (
    <Form
      onSubmit={handleFillHistorySearch}
      render={({ handleSubmit, invalid, submitting }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container columnSpacing={2}>
            <Grid item sx={{ width: 300 }}>
              <TextField
                name='ndc'
                label='Search By Original NDC'
                required
                id='NdcFillHistory-ndcSearch-input'
                disabled={submitting}
              />
            </Grid>

            <Grid item sx={{ width: 220 }}>
              <CustomDateRangeField />
            </Grid>

            <Grid item sx={{ width: 120 }}>
              <LoadingButton
                loading={submitting}
                disabled={invalid || submitting}
                type='submit'
                variant='contained'
                fullWidth
                sx={{ height: 40, mt: 2 }}
              >
                Search
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      )}
    />
  );
};

export default NdcSearch;
